import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useStoryArchitectSessionById,
  useCreateBeatForSession,
  useUpdateBeatForSession,
  useReorderBeatForSession,
  useDeleteBeatForSession,
  useSessionCharacters,
  useAddSessionCharacter,
  useRemoveSessionCharacter,
  type BeatDto,
} from '../api/queries/storyArchitect';
import {
  useConflicts,
  useCreateConflict,
  useDeleteConflict,
} from '../api/queries/conflicts';
import { useProject } from '../api/queries/projects';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { ProjectBreadcrumb } from '../components/common/ProjectBreadcrumb';
import { BeatTimeline } from '../components/storyArchitect/BeatTimeline';
import { BeatDetailPanel } from '../components/storyArchitect/BeatDetailPanel';
import { EmotionalArcChart } from '../components/storyArchitect/EmotionalArcChart';
import { CharacterSection } from '../components/characters/CharacterSection';
import { ConflictSection } from '../components/characters/ConflictSection';
import { GenerationModal } from '../components/ai/GenerationModal';

export function StoryArchitectDetailRoute() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const {
    data: session,
    isLoading,
    isError,
  } = useStoryArchitectSessionById(sessionId);
  const navigate = useNavigate();

  const createBeat = useCreateBeatForSession(sessionId);
  const updateBeat = useUpdateBeatForSession(sessionId);
  const reorderBeat = useReorderBeatForSession(sessionId);
  const deleteBeat = useDeleteBeatForSession(sessionId);

  // Characters
  const { data: characters = [] } = useSessionCharacters(sessionId);
  const addCharacter = useAddSessionCharacter(sessionId);
  const removeCharacter = useRemoveSessionCharacter(sessionId);

  // Conflicts
  const { data: conflicts = [] } = useConflicts({ sessionId });
  const createConflict = useCreateConflict();
  const deleteConflict = useDeleteConflict();

  const [selectedBeat, setSelectedBeat] = useState<BeatDto | null>(null);
  const [showArc, setShowArc] = useState(false);
  const [suggestNextOpen, setSuggestNextOpen] = useState(false);
  const [generateFromSynopsisOpen, setGenerateFromSynopsisOpen] =
    useState(false);
  const [addBeatOpen, setAddBeatOpen] = useState(false);
  const [newBeatName, setNewBeatName] = useState('');
  const [newBeatType, setNewBeatType] = useState('custom');

  const beats = useMemo(() => session?.beats ?? [], [session]);

  const projectId = session?.projectId ?? undefined;
  const { data: project } = useProject(projectId);

  const handleAddBeat = async () => {
    if (!session || !newBeatName.trim()) return;
    await createBeat.mutateAsync({
      sessionId: session.id,
      name: newBeatName.trim(),
      beatType: newBeatType,
    });
    setNewBeatName('');
    setNewBeatType('custom');
    setAddBeatOpen(false);
  };

  const handleTimelineReorder = (id: string, newIndex: number) => {
    void reorderBeat.mutateAsync({ id, newSequenceOrder: newIndex });
  };

  const handleTimelineSelect = (id: string) => {
    const beat = beats.find((b) => b.id === id) ?? null;
    setSelectedBeat(beat);
  };

  // AI prompt builders
  const suggestNextPrompt = useMemo(() => {
    if (beats.length === 0) return 'Suggest the first beat for a new story.';
    const beatList = beats
      .map(
        (b) =>
          `- ${b.name} (${b.beatType ?? 'beat'}): ${b.description ?? 'No description'}`,
      )
      .join('\n');
    return `Based on the following story beats, suggest the next logical beat in the narrative structure. Provide a title, type (setup/rising_action/climax/etc.), and 2-3 sentence description.\n\nCurrent beats:\n${beatList}`;
  }, [beats]);

  const generateFromSynopsisPrompt = useMemo(() => {
    return 'Create a complete story structure with 8-12 beats. For each beat provide: title, type, and description.\n\n(Describe your story in the instruction field)';
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !session) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate({ to: '/story-architect' })}
          >
            Back to Story Architect
          </Button>
          <Alert severity="error">
            {isError ? 'Failed to load session.' : 'Session not found.'}
          </Alert>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {project && projectId && (
        <ProjectBreadcrumb
          projectId={projectId}
          projectName={project.name}
          currentPage="Story Architect"
        />
      )}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate({ to: '/story-architect' })}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {session.name || 'Story Architect Session'}
        </Typography>
        {session.projectId && (
          <Tooltip title="Back to Project Dashboard">
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() =>
                navigate({
                  to: '/projects/$projectId',
                  params: { projectId: session.projectId! },
                })
              }
            >
              Project Dashboard
            </Button>
          </Tooltip>
        )}
      </Stack>

      {session.synopsis && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, ml: 7 }}
        >
          {session.synopsis}
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => setAddBeatOpen(true)}
        >
          + Beat
        </Button>
        <Tooltip title="Suggest next beat (AI)">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSuggestNextOpen(true)}
          >
            Suggest Next Beat
          </Button>
        </Tooltip>
        <Tooltip title="Generate beats from synopsis (AI)">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setGenerateFromSynopsisOpen(true)}
          >
            Generate from Synopsis
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Toggle emotional arc chart">
          <IconButton
            color={showArc ? 'primary' : 'default'}
            onClick={() => setShowArc(!showArc)}
          >
            <TimelineIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Emotional Arc Chart */}
      <Collapse in={showArc}>
        <Box
          sx={{
            mb: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Emotional Arc
          </Typography>
          <EmotionalArcChart beats={beats} />
        </Box>
      </Collapse>

      {beats.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center' }}
        >
          No beats yet. Click "+ Beat" to start outlining your story, or use AI
          to generate beats.
        </Typography>
      ) : (
        <BeatTimeline
          beats={beats}
          selectedBeatId={selectedBeat?.id ?? null}
          onSelectBeat={handleTimelineSelect}
          onReorderBeat={handleTimelineReorder}
        />
      )}

      {/* Characters & Conflicts */}
      <Box sx={{ mt: 3 }}>
        <CharacterSection
          characters={characters}
          onAdd={(entityId, role) => addCharacter.mutate({ entityId, role })}
          onRemove={(id) => removeCharacter.mutate(id)}
        />
      </Box>
      <Box sx={{ mt: 1 }}>
        <ConflictSection
          conflicts={conflicts}
          onCreate={(input) => createConflict.mutate({ ...input, sessionId })}
          onDelete={(id) => deleteConflict.mutate(id)}
        />
      </Box>

      {/* Beat detail panel */}
      <BeatDetailPanel
        beat={selectedBeat}
        open={!!selectedBeat}
        onClose={() => setSelectedBeat(null)}
        onSave={(updates) => {
          updateBeat.mutate(updates);
          setSelectedBeat(null);
        }}
        onDelete={(id) => {
          deleteBeat.mutate(id);
          setSelectedBeat(null);
        }}
      />

      {/* AI: Suggest Next Beat modal */}
      <GenerationModal
        open={suggestNextOpen}
        onClose={() => setSuggestNextOpen(false)}
        mode="text"
        seedPrompt={suggestNextPrompt}
      />

      {/* AI: Generate from Synopsis modal */}
      <GenerationModal
        open={generateFromSynopsisOpen}
        onClose={() => setGenerateFromSynopsisOpen(false)}
        mode="text"
        seedPrompt={generateFromSynopsisPrompt}
      />

      {/* Add Beat dialog */}
      <Dialog
        open={addBeatOpen}
        onClose={() => setAddBeatOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Beat</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Beat name"
              placeholder='e.g. "Inciting incident" or "The betrayal revealed"'
              value={newBeatName}
              onChange={(e) => setNewBeatName(e.target.value)}
              autoFocus
              fullWidth
            />
            <FilterableSelect
              label="Beat type"
              value={newBeatType}
              onChange={(newValue) => setNewBeatType(newValue || 'custom')}
              allowEmpty={false}
              options={[
                { value: 'setup', label: 'Setup' },
                { value: 'inciting_incident', label: 'Inciting Incident' },
                { value: 'rising_action', label: 'Rising Action' },
                { value: 'midpoint', label: 'Midpoint' },
                { value: 'climax', label: 'Climax' },
                { value: 'falling_action', label: 'Falling Action' },
                { value: 'resolution', label: 'Resolution' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBeatOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddBeat}
            variant="contained"
            disabled={!newBeatName.trim() || createBeat.isPending}
          >
            {createBeat.isPending ? 'Adding...' : 'Add Beat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
